import { Database, HardDrive, RotateCcw, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import {
  clearApplicationDatabases,
  formatBytes,
  getStorageSummary,
  requestPersistentStorage,
  type StorageSummary,
} from "../../storage/storageAdmin";

export function SettingsPage() {
  const [summary, setSummary] = useState<StorageSummary | null>(null);
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(false);

  const refresh = () => {
    void getStorageSummary().then(setSummary);
  };

  useEffect(refresh, []);

  const requestPersistence = async () => {
    setWorking(true);
    const persisted = await requestPersistentStorage();
    setMessage(
      persisted
        ? "Persistent storage was granted by this browser."
        : "Persistent storage was not granted. Local data remains best effort.",
    );
    setWorking(false);
    refresh();
  };

  const clearAll = async () => {
    const confirmed = window.confirm(
      "Clear all local quotation, QR, and link records? This cannot be undone. Cloud links are not revoked by clearing local data.",
    );
    if (!confirmed) {
      return;
    }

    setWorking(true);
    try {
      await clearApplicationDatabases();
      setMessage(
        "Local application databases were cleared. Existing cloud links were not revoked.",
      );
      refresh();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Local data could not be cleared.",
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="settings-grid">
      <section className="settings-card">
        <div className="section-heading">
          <HardDrive aria-hidden="true" />
          <div>
            <h2>Local storage</h2>
            <p>Drafts and transfer progress are kept on this device.</p>
          </div>
        </div>
        <dl className="definition-list">
          <div>
            <dt>Used</dt>
            <dd>{summary ? formatBytes(summary.usage) : "Checking..."}</dd>
          </div>
          <div>
            <dt>Browser estimate</dt>
            <dd>{summary ? formatBytes(summary.quota) : "Checking..."}</dd>
          </div>
          <div>
            <dt>Persistence</dt>
            <dd>{summary?.persisted ? "Granted" : "Best effort"}</dd>
          </div>
        </dl>
        <button
          className="button button-secondary"
          type="button"
          disabled={working}
          onClick={() => void requestPersistence()}
        >
          <ShieldCheck aria-hidden="true" size={18} />
          Request persistent storage
        </button>
      </section>

      <section className="settings-card danger-zone">
        <div className="section-heading">
          <Database aria-hidden="true" />
          <div>
            <h2>Clear local data</h2>
            <p>
              This removes browser drafts and progress. It does not revoke an encrypted
              link already created.
            </p>
          </div>
        </div>
        <button
          className="button button-danger"
          type="button"
          disabled={working}
          onClick={() => void clearAll()}
        >
          <RotateCcw aria-hidden="true" size={18} />
          Clear all local data
        </button>
      </section>

      {message ? (
        <p className="status-banner" role="status">
          {message}
        </p>
      ) : null}
    </div>
  );
}
